import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlay } from 'react-icons/fa';
import { IoShareOutline } from 'react-icons/io5';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/AlbumDetail.css';

const AlbumDetail = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const { handleTrackClick, playAlbum } = useContext(AudioContext);
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinnedTracks, setPinnedTracks] = useState([]);
  const [plan, setPlan] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        const firebaseUid = auth.currentUser?.uid;
        const albumResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/album-details/${albumId}`
        );
        setAlbum(albumResponse.data);

        const pinnedResponse = await axios.get(
          `https://sync-api-troz.onrender.com/api/uploads/pinned`,
          { params: { firebaseUid } }
        );
        setPinnedTracks(pinnedResponse.data);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching album:', error);
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [albumId]);

  const handlePinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/pin', {
        firebaseUid,
        trackId,
      });
      const pinnedResponse = await axios.get(
        `https://sync-api-troz.onrender.com/api/uploads/pinned?firebaseUid=${firebaseUid}`
      );
      setPinnedTracks(pinnedResponse.data);
    } catch (error) {
      console.error('Error pinning track:', error);
    }
  };

  const handleUnpinTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/unpin', {
        firebaseUid,
        trackId,
      });
      setPinnedTracks((prev) => prev.filter((track) => track._id !== trackId));
    } catch (error) {
      console.error('Error unpinning track:', error);
    }
  };

  const handleDeleteTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (window.confirm('Are you sure you want to delete this track?')) {
      try {
        await axios.delete('https://sync-api-troz.onrender.com/api/uploads/delete-track', {
          data: { trackId, firebaseUid },
        });
        window.alert('Track deleted successfully');
        setAlbum((prev) => ({
          ...prev,
          tracks: prev.tracks.filter((track) => track._id !== trackId),
        }));
      } catch (error) {
        console.error('Error deleting track:', error);
        window.alert('Failed to delete track');
      }
    }
  };

  const handleLikeTrack = async (trackId) => {
    window.alert('Like functionality not implemented yet.');
  };

  const handlePlayAlbum = () => {
    if (album && album.tracks.length > 0) {
      playAlbum(albumId);
    } else {
      window.alert('No tracks available to play in this album.');
    }
  };

  const handleShareAlbum = async () => {
    if (!albumId || !album) {
      window.alert('No album available to share.');
      return;
    }

    const firebaseUid = auth.currentUser?.uid;
    const deepLink = `https://syncmusic.app/otheruserdiscography/${firebaseUid}/album/${albumId}`;
    const message = `🎵 Check out this album: "${album.albumTitle}" by ${album.artistName}\n\n🔥 Listen now: ${deepLink}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Share ${album.albumTitle}`,
          text: message,
          url: deepLink,
        });
      } else {
        await navigator.clipboard.writeText(message);
        window.alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing album:', error);
      window.alert('Failed to share album.');
    }
  };

  if (loading) {
    return (
      <div className="album-detail-loading">
        <div className="loader"></div>
        <p>Loading album...</p>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="album-detail-loading">
        <p>Album not found.</p>
      </div>
    );
  }

  return (
    <div className="album-detail-page">
      <div className="album-detail-container">
        <div className="album-banner">
          {album.coverPhoto ? (
            <img src={album.coverPhoto} alt="Album Cover" className="album-banner-image" />
          ) : (
            <div className="album-banner-placeholder" />
          )}
          <p className="album-title">{album.albumTitle}</p>
        </div>

        <div className="header-container">
          <button onClick={() => navigate(-1)} className="back-button">
            <FaArrowLeft size={24} />
          </button>
          <p className="header-text">{album.artistName}</p>
          <button onClick={handlePlayAlbum} className="play-button">
            <FaPlay size={18} />
          </button>
          <button onClick={handleShareAlbum} className="share-button">
            <IoShareOutline size={24} />
          </button>
        </div>

        <div className="tracks-container">
          {album.tracks && album.tracks.length > 0 ? (
            album.tracks.map((track) => (
              <TrackItem
                key={track._id}
                track={track}
                onPlay={() => handleTrackClick(track)}
                onPin={() => handlePinTrack(track._id)}
                onUnpin={() => handleUnpinTrack(track._id)}
                isPinned={pinnedTracks.some((t) => t._id === track._id)}
                onDelete={() => handleDeleteTrack(track._id)}
                onLike={() => handleLikeTrack(track._id)}
                currentUserId={auth.currentUser?.uid}
              />
            ))
          ) : (
            <p className="no-tracks-text">No tracks in this album.</p>
          )}
          <div className="spacer"></div>
        </div>
      </div>
    </div>
  );
};

export default AlbumDetail;